import { formatDate } from '@clim/client-core';
import { INVOICE_ACTIONS, INVOICE_STATUS_LABELS, INVOICE_STATUS_TONES, formatEuros, isoDate, type InvoiceEntryRecord, type InvoiceStatus } from '@clim/domain';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Empty, ErrorBox, Field, Input, Modal, Textarea } from '../components/ui';
import { downloadFile } from '../lib/download';
import { errorMessage } from '../lib/errors';
import { useReady } from '../lib/session';
import { useTerritory } from '../lib/territory';
import { useOnline } from '../lib/use-online';

interface Queue {
  connector: { id: string; label: string; pushes: boolean };
  entries: InvoiceEntryRecord[];
  counts: Partial<Record<InvoiceStatus, number>>;
}
const TABS: InvoiceStatus[] = ['to_invoice', 'exported', 'to_regularize', 'invoiced', 'on_hold', 'excluded', 'regularized', 'cancelled'];
const Badge = ({ s }: { s: InvoiceStatus }) => <span className={`badge tone-${INVOICE_STATUS_TONES[s]}`}>{INVOICE_STATUS_LABELS[s]}</span>;

/**
 * Facturation (§10) : la file « À facturer » alimente votre logiciel de facturation (lot CSV), qui attribue les
 * numéros de facture ; vous les reportez ici. Krotime n'émet aucune facture (pas de double comptabilité).
 */
export function InvoicingPage() {
  const { api } = useReady();
  const online = useOnline();
  const { territoryId } = useTerritory();
  const [tab, setTab] = useState<InvoiceStatus>('to_invoice');
  const [q, setQ] = useState<Queue | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<InvoiceEntryRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    if (!online) return;
    api.request<Queue>('GET', `/v1/invoicing?status=${tab}${territoryId ? `&territoryId=${territoryId}` : ''}`).then(
      (r) => (setQ(r), setError(null)),
      (e) => setError(errorMessage(e)),
    );
  }, [api, online, tab, territoryId]);
  useEffect(load, [load]);
  useEffect(() => setSelected(new Set()), [tab]);
  const list = q?.entries ?? [];
  const exportSel = async () => {
    setError(null);
    try {
      const csv = await api.request<string>('POST', '/v1/invoicing/export', { ids: [...selected] });
      downloadFile(csv, `a-facturer-${isoDate(new Date())}.csv`, 'text/csv;charset=utf-8');
      setSelected(new Set());
      load();
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const toggle = (id: string) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setSelected(s);
  };
  const total = list.filter((e) => selected.has(e.id)).reduce((s, e) => s + e.totalTtcCents, 0);

  return (
    <div>
      <h1>Facturation</h1>
      <p className="muted">
        Dossiers validés (après contrôle) à transmettre à votre logiciel de facturation. Exportez-les ({q?.connector.label ?? 'tableur'}), créez les factures dans
        le logiciel, puis reportez ici leur numéro. Krotime n’émet ni ne numérote aucune facture.
      </p>
      <div className="chips" role="tablist" aria-label="Statut">
        {TABS.map((s) => (
          <button key={s} role="tab" aria-selected={tab === s} className={`chip${tab === s ? ' on' : ''}`} onClick={() => setTab(s)}>
            {INVOICE_STATUS_LABELS[s]}
            {q?.counts[s] ? ` (${q.counts[s]})` : ''}
          </button>
        ))}
      </div>
      {!online && <Empty>La facturation nécessite une connexion.</Empty>}
      <ErrorBox message={error} />
      {selected.size > 0 && (
        <div className="card selection-bar">
          <strong>
            {selected.size} dossier(s) · {formatEuros(total)} TTC
          </strong>
          <button className="btn primary small" onClick={() => void exportSel()}>
            Exporter la sélection (CSV)
          </button>
        </div>
      )}
      {q && !list.length && <Empty>Rien dans « {INVOICE_STATUS_LABELS[tab]} ».</Empty>}
      {list.length > 0 && (
        <table className="table" aria-label="File de facturation">
          <thead>
            <tr>
              <th>
                {INVOICE_ACTIONS.export.includes(tab) && (
                  <input type="checkbox" aria-label="Tout sélectionner" checked={list.every((e) => selected.has(e.id))} onChange={(ev) => setSelected(ev.target.checked ? new Set(list.map((e) => e.id)) : new Set())} />
                )}
              </th>
              <th>Dossier</th>
              <th>Validé le</th>
              <th>Total TTC</th>
              <th>Acomptes</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {list.map((e) => (
              <tr key={e.id}>
                <td>{INVOICE_ACTIONS.export.includes(e.status) && <input type="checkbox" aria-label={`Sélectionner ${e.reference}`} checked={selected.has(e.id)} onChange={() => toggle(e.id)} />}</td>
                <td className="clickable" onClick={() => setOpen(e)}>
                  <strong>{e.clientName}</strong>
                  <div className="muted">{e.reference}</div>
                </td>
                <td className="nowrap">{formatDate(e.validatedAt)}</td>
                <td className="nowrap">{formatEuros(e.totalTtcCents)}</td>
                <td className="nowrap">{e.paidCents ? formatEuros(e.paidCents) : '—'}</td>
                <td>
                  <Badge s={e.status} />
                  {e.externalNumber && <div className="muted">Facture {e.externalNumber}</div>}
                  {e.note && <div className="muted">{e.note}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {open && (
        <EntryDialog
          entry={open}
          onClose={() => setOpen(null)}
          onChanged={() => {
            setOpen(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function EntryDialog({ entry: e, onClose, onChanged }: { entry: InvoiceEntryRecord; onClose(): void; onChanged(): void }) {
  const { api } = useReady();
  const [number, setNumber] = useState('');
  const [on, setOn] = useState(isoDate(new Date()));
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const act = async (path: string, body: unknown = {}) => {
    setError(null);
    try {
      await api.request('POST', `/v1/invoicing/${e.id}/${path}`, body);
      onChanged();
    } catch (err) {
      setError(errorMessage(err));
    }
  };
  const s = e.snapshot;
  return (
    <Modal title={`${e.reference} — ${e.clientName}`} onClose={onClose}>
      <p>
        <Badge s={e.status} /> <Link to={`/dossiers/${e.dossierId}`}>Ouvrir le dossier</Link>
      </p>
      <dl className="kv">
        <dt>Client</dt>
        <dd>
          {s.client.name}
          {s.client.siret ? ` · SIRET ${s.client.siret}` : ''}
          <div className="muted">{[s.client.address, s.client.email, s.client.phone].filter(Boolean).join(' · ')}</div>
        </dd>
        {s.dossier.siteAddress && (
          <>
            <dt>Intervention</dt>
            <dd>{s.dossier.siteAddress}</dd>
          </>
        )}
        <dt>Commercial</dt>
        <dd>{s.dossier.commercial ?? '—'}</dd>
      </dl>
      <table className="table compact" aria-label="Lignes à facturer">
        <tbody>
          {s.lines.map((l, i) => (
            <tr key={i}>
              <td>
                {l.label}
                {l.model ? <div className="muted">{l.model}</div> : null}
              </td>
              <td className="nowrap">× {String(l.quantity).replace('.', ',')}</td>
              <td className="nowrap">{formatEuros(l.totalHtCents)} HT</td>
            </tr>
          ))}
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td />
            <td className="nowrap">
              {formatEuros(s.totals.htCents)} HT · <strong>{formatEuros(s.totals.ttcCents)} TTC</strong>
            </td>
          </tr>
          {s.paidCents > 0 && (
            <tr>
              <td>Acomptes encaissés</td>
              <td />
              <td className="nowrap">{formatEuros(s.paidCents)}</td>
            </tr>
          )}
        </tbody>
      </table>
      <ErrorBox message={error} />
      {INVOICE_ACTIONS.invoiced.includes(e.status) && (
        <div className="form">
          <div className="row-inline">
            <Field label="N° de facture (logiciel de facturation)">
              <Input value={number} onChange={(ev) => setNumber(ev.target.value)} aria-label="Numéro de facture" />
            </Field>
            <Field label="Date de facture">
              <Input type="date" value={on} onChange={(ev) => ev.target.value && setOn(ev.target.value)} />
            </Field>
          </div>
          <button className="btn primary" disabled={!number.trim()} onClick={() => void act('invoiced', { externalNumber: number, invoicedOn: on })}>
            Marquer facturé
          </button>
        </div>
      )}
      {(INVOICE_ACTIONS.exclude.includes(e.status) || INVOICE_ACTIONS.regularize.includes(e.status)) && (
        <div className="form">
          <Field label={e.status === 'to_regularize' ? 'Régularisation effectuée (ex. avoir n°…)' : 'Motif d’exclusion'}>
            <Textarea value={text} onChange={(ev) => setText(ev.target.value)} />
          </Field>
          {e.status === 'to_regularize' ? (
            <button className="btn primary" disabled={text.trim().length < 3} onClick={() => void act('regularize', { comment: text })}>
              Marquer régularisé
            </button>
          ) : (
            <button className="btn" disabled={text.trim().length < 3} onClick={() => void act('exclude', { reason: text })}>
              Exclure de la facturation
            </button>
          )}
        </div>
      )}
      {INVOICE_ACTIONS.restore.includes(e.status) && (
        <button className="btn" onClick={() => void act('restore')}>
          Remettre « À facturer »
        </button>
      )}
      <h2>Historique</h2>
      <ul className="timeline">
        {e.events.map((ev, i) => (
          <li key={i}>
            <strong>{formatDate(ev.at)}</strong> · {ev.byName}
            {ev.comment ? ` — ${ev.comment}` : ''}
          </li>
        ))}
      </ul>
    </Modal>
  );
}
